import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import api from '@/utils/api';

const AuthContext = createContext();

// 30 minutes idle timeout
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const idleTimerRef = useRef(null);

  const logout = useCallback((reason) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setPermissions([]);
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (reason === 'idle') {
      // Don't show alert during loading — display info on next login screen
      localStorage.setItem('logout_reason', 'idle');
    }
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => logout('idle'), IDLE_TIMEOUT_MS);
  }, [logout]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      fetchUserProfile();
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set up idle detection when user is logged in
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    const handleActivity = () => resetIdleTimer();

    events.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));
    resetIdleTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, resetIdleTimer]);

  const fetchUserProfile = async () => {
    try {
      const [meRes, permsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/me/permissions').catch(() => ({ data: { permissions: [] } })),
      ]);
      setUser(meRes.data);
      setPermissions(permsRes.data.permissions || []);
      localStorage.setItem('user', JSON.stringify(meRes.data));
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      logout();
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.removeItem('logout_reason');
    setUser(userData);
    // Fetch permissions right after login
    try {
      const permsRes = await api.get('/me/permissions');
      setPermissions(permsRes.data.permissions || []);
    } catch {
      setPermissions([]);
    }
    
    return userData;
  };

  const hasPermission = useCallback(
    (perm) => {
      if (!user) return false;
      if (user.role === 'super_admin') return true;
      if (!perm) return true;
      if (Array.isArray(perm)) return perm.some((p) => permissions.includes(p));
      return permissions.includes(perm);
    },
    [user, permissions]
  );

  const value = {
    user,
    permissions,
    hasPermission,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
